import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Newspaper, FileText, DollarSign, Tag, AlertTriangle, Calendar, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryConfig = {
  document: { label: 'Document', icon: FileText, color: 'bg-violet-100 text-violet-700 border-violet-200' },
  commission: { label: 'Commission', icon: DollarSign, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  tarif: { label: 'Tarif', icon: Tag, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  general: { label: 'Général', icon: Newspaper, color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${API}/news`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setNews(data);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const importantNews = news.filter(n => n.is_important);
  const regularNews = news.filter(n => !n.is_important);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="news-title">
          Actualités
        </h1>
        <p className="text-slate-500 mt-1">Restez informé des dernières nouveautés</p>
      </div>

      {/* Important announcements */}
      {importantNews.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Annonces importantes
          </h2>
          {importantNews.map((item, index) => {
            const CategoryIcon = categoryConfig[item.category]?.icon || Newspaper;
            return (
              <Card 
                key={item.news_id}
                className="border-0 shadow-sm border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/20 animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`important-news-${item.news_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                      <CategoryIcon className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                          {item.title}
                        </h3>
                        <Badge className={`${categoryConfig[item.category]?.color} border`}>
                          {categoryConfig[item.category]?.label}
                        </Badge>
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 mb-3">
                        {item.content}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {new Date(item.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Regular news */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-indigo-500" />
          Dernières actualités
        </h2>
        
        {regularNews.length === 0 && importantNews.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Newspaper className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">Aucune actualité pour le moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {regularNews.map((item, index) => {
              const CategoryIcon = categoryConfig[item.category]?.icon || Newspaper;
              return (
                <Card 
                  key={item.news_id}
                  className="stat-card border-0 shadow-sm animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`news-${item.news_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800">
                        <CategoryIcon className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-900 dark:text-white">
                            {item.title}
                          </h3>
                          <Badge variant="outline" className={`${categoryConfig[item.category]?.color} border`}>
                            {categoryConfig[item.category]?.label}
                          </Badge>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                          {item.content}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {new Date(item.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
